import React from 'react';
import cn from 'classnames';
import Chip from '@ifit/mp-chip';
import LineWrap from '@ifit/mp-line-wrap';
import Estimates from '@ifit/mp-estimates';
import Icon from '@ifit/react-icons/dist/components/icon';
import STAR_SOLID from '@ifit/react-icons/dist/icons/STAR_SOLID';
import LOCKED_SOLID from '@ifit/react-icons/dist/icons/LOCKED_SOLID';
import { DefaultImages } from '@ifit/img/out/defaults';
import { useEquipmentType } from '../../../state-management/selectors/use-equipment-type';
import { useUseMetricUnits } from '../../../state-management/selectors/use-use-metric-units';
import { globalizeCompletedDate } from '../../../util/date-utils';
import { getResizedImageUrl } from '../../../util/get-resized-image-url';
import './index.scss';

interface IEstimates {
  time: number | string;
  distance: number | string;
  calories: number | string;
}

export interface ICardWorkoutProps {
  imageUrl: string;
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
  tagPrimary: string;
  title: string;
  averageRating?: string;
  className?: string;
  completed?: string;
  estimates?: IEstimates;
  locked?: boolean;
  recommended?: boolean;
  themeClass?: string;
}

export default function CardWorkout({
  averageRating,
  className,
  completed,
  estimates,
  imageUrl,
  locked,
  onClick,
  recommended,
  tagPrimary,
  themeClass,
  title,
  ...rest
}: ICardWorkoutProps): JSX.Element {
  const equipmentType = useEquipmentType();
  const metricUnits = useUseMetricUnits();
  const isMetricUnit = metricUnits === 'true' || (metricUnits as unknown as boolean) === true;
  const resizedImageUrl = getResizedImageUrl(imageUrl, DefaultImages.LargeCard, {
    width: 344,
    height: 212,
  });

  return (
    <a
      href="#"
      onClick={onClick}
      className={cn('card-workout', themeClass, className, {
        locked: Boolean(locked),
      })}
      data-testid="card-workout"
      {...rest}
    >
      <article className="card-workout-article">
        <div className="card-workout-image-container">
          <img src={resizedImageUrl} alt="" className="card-workout-image" />

          <div className="card-workout-tags">
            <div
              className={cn('card-workout-tag-primary', {
                ['recommended-wrapper']: recommended,
              })}
              data-testid="card-workout-tag-primary"
            >
              {completed && (
                <span className="card-workout-completed" data-testid="card-workout-completed">
                  <Chip>{globalizeCompletedDate(completed)}</Chip>
                </span>
              )}
              {tagPrimary && !completed && (
                <Chip recommended={recommended}>
                  {tagPrimary}
                  {recommended && locked && (
                    <Icon
                      icon={LOCKED_SOLID}
                      size={18}
                      className="card-workout-locked-icon"
                      data-testid="card-workout-locked-icon"
                    />
                  )}
                </Chip>
              )}
            </div>

            {locked && !recommended && (
              <div className="locked-chip">
                <Chip>
                  <Icon
                    icon={LOCKED_SOLID}
                    size={18}
                    className="card-workout-locked-icon"
                    data-testid="card-workout-locked-icon"
                  />
                </Chip>
              </div>
            )}
          </div>
        </div>

        <div className="card-workout-details">
          <header className="card-workout-header">
            <h2 className="card-workout-title">
              <LineWrap text={title} maxLines={2} basedOn="words" />
            </h2>
          </header>

          {estimates && (
            <Estimates
              estimates={estimates}
              themeClass="card-workout-estimates"
              iconColor={null}
              limit={null}
              equipmentType={equipmentType}
              useMetricUnits={isMetricUnit}
            />
          )}

          {averageRating && (
            <span className="card-workout-average-rating" data-testid="card-workout-average-rating">
              <Icon icon={STAR_SOLID} size={16} className="card-workout-average-rating-icon" />
              {averageRating}
            </span>
          )}
        </div>
      </article>
    </a>
  );
}
